import { PHYSICS_VERSION, PROTOCOL_VERSION } from "@air-hockey/game-core";
import { resolveWebSocketUrl } from "./api";
import type { ConnectionMetrics, ServerSnapshot, Vector2 } from "./types";

export type ServerMessage =
  | ServerSnapshot
  | {
      type: "WELCOME";
      player?: "PLAYER_A" | "PLAYER_B";
      playerId?: string;
      matchId?: string;
      protocolVersion?: string;
      physicsVersion?: string;
      reconnectTicket?: string;
      reconnectTicketExpiresAt?: string | number;
      [key: string]: unknown;
    }
  | { type: "MATCH_STATE"; state?: unknown; [key: string]: unknown }
  | { type: "PLAYER_CONNECTION"; player?: "PLAYER_A" | "PLAYER_B"; connected?: boolean; [key: string]: unknown }
  | { type: "COUNTDOWN"; remainingTicks?: number; [key: string]: unknown }
  | { type: "GOAL"; scorer?: "PLAYER_A" | "PLAYER_B"; scoreA?: number; scoreB?: number; [key: string]: unknown }
  | { type: "FINISHED"; winner?: "PLAYER_A" | "PLAYER_B" | null; scoreA?: number; scoreB?: number; [key: string]: unknown }
  | { type: "PONG"; clientTime?: number; serverTime?: number; [key: string]: unknown }
  | { type: "ERROR"; code?: string; message?: string; [key: string]: unknown };

type MessageListener = (message: ServerMessage) => void;
type MetricsListener = (metrics: ConnectionMetrics) => void;
type WebSocketFactory = (url: string) => WebSocket;

export interface SentInput {
  sequence: number;
  clientTime: number;
  target: Vector2;
}

type InputListener = (input: SentInput) => void;

export interface ResumeCredential {
  wsUrl: string;
  expiresAt: number;
  protocolVersion: string;
  physicsVersion: string;
}

type ResumeListener = (credential: ResumeCredential) => void;

const MAX_RECONNECT_WINDOW_MS = 15_000;
const INPUT_INTERVAL_MS = 1_000 / 60;

export class NetworkSession {
  private readonly messageListeners = new Set<MessageListener>();
  private readonly metricsListeners = new Set<MetricsListener>();
  private readonly inputListeners = new Set<InputListener>();
  private readonly resumeListeners = new Set<ResumeListener>();
  private socket: WebSocket | null = null;
  private url = "";
  private closedByClient = false;
  private reconnectStartedAt = 0;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pendingInput: Vector2 | null = null;
  private inputTimer: ReturnType<typeof setTimeout> | null = null;
  private lastInputAt = 0;
  private sequence = 0;
  private metrics: ConnectionMetrics = {
    status: "idle",
    pingMs: null,
    reconnectAttempt: 0,
    message: undefined,
  };

  constructor(private readonly makeWebSocket: WebSocketFactory = (url) => new WebSocket(url)) {}

  connect(wsUrl: string): void {
    this.closeSocket();
    this.closedByClient = false;
    this.url = resolveWebSocketUrl(wsUrl);
    this.reconnectStartedAt = 0;
    this.reconnectAttempt = 0;
    this.openSocket(false);
  }

  subscribeMessages(listener: MessageListener): () => void {
    this.messageListeners.add(listener);
    return () => this.messageListeners.delete(listener);
  }

  subscribeMetrics(listener: MetricsListener): () => void {
    this.metricsListeners.add(listener);
    listener(this.metrics);
    return () => this.metricsListeners.delete(listener);
  }

  subscribeSentInputs(listener: InputListener): () => void {
    this.inputListeners.add(listener);
    return () => this.inputListeners.delete(listener);
  }

  subscribeResume(listener: ResumeListener): () => void {
    this.resumeListeners.add(listener);
    return () => this.resumeListeners.delete(listener);
  }

  sendReady(): void {
    this.send({ type: "READY", protocolVersion: PROTOCOL_VERSION, physicsVersion: PHYSICS_VERSION });
  }

  sendInput(target: Vector2): void {
    this.pendingInput = target;
    const now = performance.now();
    const wait = INPUT_INTERVAL_MS - (now - this.lastInputAt);
    if (wait <= 0) {
      this.flushInput();
      return;
    }
    if (this.inputTimer === null) {
      this.inputTimer = setTimeout(() => {
        this.inputTimer = null;
        this.flushInput();
      }, wait);
    }
  }

  forfeit(reason = "player_left"): void {
    this.send({ type: "FORFEIT", reason });
  }

  close(): void {
    this.closedByClient = true;
    this.clearTimers();
    this.closeSocket();
    this.setMetrics({ status: "closed", reconnectAttempt: 0 });
  }

  private openSocket(isReconnect: boolean): void {
    if (!this.url || this.closedByClient) return;
    this.setMetrics({
      status: isReconnect ? "reconnecting" : "connecting",
      reconnectAttempt: this.reconnectAttempt,
    });

    let socket: WebSocket;
    try {
      socket = this.makeWebSocket(this.url);
    } catch (error) {
      this.handleOpenFailure(error);
      return;
    }
    this.socket = socket;
    socket.binaryType = "arraybuffer";

    socket.addEventListener("open", () => {
      if (this.socket !== socket) return;
      this.reconnectStartedAt = 0;
      this.reconnectAttempt = 0;
      this.setMetrics({ status: "open", reconnectAttempt: 0, message: undefined });
      this.startPing();
    });

    socket.addEventListener("message", (event) => {
      void this.handleRawMessage(event.data);
    });

    socket.addEventListener("error", () => {
      if (this.socket === socket) {
        this.setMetrics({ status: "error", reconnectAttempt: this.reconnectAttempt, message: "Network connection failed." });
      }
    });

    socket.addEventListener("close", () => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.stopPing();
      if (!this.closedByClient) this.scheduleReconnect();
    });
  }

  private async handleRawMessage(raw: unknown): Promise<void> {
    try {
      const text =
        typeof raw === "string"
          ? raw
          : raw instanceof ArrayBuffer
            ? new TextDecoder().decode(raw)
            : raw instanceof Blob
              ? await raw.text()
              : "";
      if (!text) return;
      const parsed = JSON.parse(text) as unknown;
      if (parsed === null || typeof parsed !== "object") return;
      const record = parsed as Record<string, unknown>;
      if (typeof record.type !== "string") return;
      const message = { ...record, type: record.type.toUpperCase() } as ServerMessage;

      if (message.type === "WELCOME") {
        if (message.protocolVersion !== PROTOCOL_VERSION || message.physicsVersion !== PHYSICS_VERSION) {
          this.rejectVersionMismatch(message.protocolVersion, message.physicsVersion);
          return;
        }
        if (typeof message.reconnectTicket === "string") {
          this.rotateReconnectTicket(message.reconnectTicket, message.reconnectTicketExpiresAt);
        }
        this.sendReady();
      }
      if (message.type === "PONG") {
        const clientTime = typeof message.clientTime === "number" ? message.clientTime : null;
        if (clientTime !== null) {
          this.setMetrics({ pingMs: Math.max(0, Math.round(performance.now() - clientTime)) });
        }
      }
      this.messageListeners.forEach((listener) => listener(message));
    } catch {
      this.setMetrics({ message: "A server message could not be read." });
    }
  }

  private flushInput(): void {
    if (!this.pendingInput) return;
    const target = this.pendingInput;
    const clientTime = performance.now();
    const sequence = this.sequence + 1;
    const sent = this.send({
      type: "INPUT",
      sequence,
      clientTime,
      targetX: target.x,
      targetY: target.y,
    });
    if (!sent) return;
    this.pendingInput = null;
    this.lastInputAt = clientTime;
    this.sequence = sequence;
    const input: SentInput = { sequence, clientTime, target };
    this.inputListeners.forEach((listener) => listener(input));
  }

  private rotateReconnectTicket(ticket: string, rawExpiresAt: unknown): void {
    try {
      const url = new URL(this.url);
      url.searchParams.set("ticket", ticket);
      this.url = url.toString();
      const expiresAt = normalizeEpochMilliseconds(rawExpiresAt);
      if (expiresAt === null || expiresAt <= Date.now()) return;
      this.resumeListeners.forEach((listener) => listener({
        wsUrl: this.url,
        expiresAt,
        protocolVersion: PROTOCOL_VERSION,
        physicsVersion: PHYSICS_VERSION,
      }));
    } catch {
      // A malformed URL will already have failed during the initial connection.
    }
  }

  private rejectVersionMismatch(protocolVersion: unknown, physicsVersion: unknown): void {
    this.closedByClient = true;
    const message = `Version mismatch (server protocol ${String(protocolVersion)}, physics ${String(physicsVersion)}).`;
    this.setMetrics({ status: "error", reconnectAttempt: 0, message });
    this.messageListeners.forEach((listener) => listener({
      type: "ERROR",
      code: "VERSION_MISMATCH",
      message,
    }));
    if (this.socket && this.socket.readyState < WebSocket.CLOSING) this.socket.close(1002, "version_mismatch");
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      this.send({ type: "PING", clientTime: performance.now() });
    }, 2_000);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  private scheduleReconnect(): void {
    const now = Date.now();
    if (this.reconnectStartedAt === 0) this.reconnectStartedAt = now;
    if (now - this.reconnectStartedAt >= MAX_RECONNECT_WINDOW_MS) {
      this.setMetrics({ status: "closed", reconnectAttempt: this.reconnectAttempt, message: "Reconnect window expired." });
      return;
    }

    this.reconnectAttempt += 1;
    const delay = Math.min(2_500, 300 * 2 ** Math.min(this.reconnectAttempt - 1, 3));
    this.setMetrics({ status: "reconnecting", reconnectAttempt: this.reconnectAttempt, message: "Reconnecting…" });
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket(true);
    }, delay);
  }

  private handleOpenFailure(error: unknown): void {
    const message = error instanceof Error ? error.message : "Could not open the WebSocket.";
    this.setMetrics({ status: "error", reconnectAttempt: this.reconnectAttempt, message });
    this.scheduleReconnect();
  }

  private send(payload: object): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(payload));
    return true;
  }

  private setMetrics(patch: Partial<ConnectionMetrics>): void {
    this.metrics = { ...this.metrics, ...patch };
    this.metricsListeners.forEach((listener) => listener(this.metrics));
  }

  private closeSocket(): void {
    if (this.socket && this.socket.readyState < WebSocket.CLOSING) this.socket.close(1000, "client_close");
    this.socket = null;
  }

  private clearTimers(): void {
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    if (this.inputTimer !== null) clearTimeout(this.inputTimer);
    this.reconnectTimer = null;
    this.inputTimer = null;
    this.stopPing();
  }
}

function normalizeEpochMilliseconds(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}
